import axios from 'axios';

/**
 * 令牌失效时拦截器广播的事件名。AuthProvider 监听它并清掉登录态，然后由
 * React Router 跳到登录页 —— 不做整页刷新，也就不必再下载/解析一遍 bundle。
 */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

// 创建axios实例
const api = axios.create({
  baseURL: '/api',
  timeout: 300000, // 5 minutes timeout for long-running operations like Playwright
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestUrl: string = error.config?.url || '';

    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');

    // 403 也要算。后端用的是 HTTPBearer(auto_error=True)：**缺少** Authorization 头
    // 返回的是 403 {"detail":"Not authenticated"}，只有令牌无效才是 401。原来只处理
    // 401，于是在另一个标签页登出（token 被清掉）之后，这个标签页会永远停在
    // "加载失败"而不跳登录页。
    // 403 也可能是"注册已关闭"这类业务拒绝，所以只在确实没有本地令牌、或后端明说
    // 未认证时才登出。
    const detail = error.response?.data?.detail;
    const notAuthenticated =
      typeof detail === 'string' && detail.toLowerCase().includes('not authenticated');
    const shouldSignOut =
      !isAuthEndpoint &&
      (status === 401 || (status === 403 && (notAuthenticated || !localStorage.getItem('token'))));

    if (shouldSignOut) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // 广播出去让 AuthProvider 把状态清掉，由 React Router 跳登录页。
      //
      // 原来这里是 window.location.reload()：一次整页重启，2.1MB 的 bundle 要重新
      // 下载（或至少重新解析执行）一遍才看到登录框。令牌有效期 30 天，所以这条路径
      // 是周期性会走到的，而且现在恢复登录态不再阻塞首屏，用户会先看到界面再被整页
      // 刷掉，比之前更刺眼。软跳转没有这个成本。
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    return Promise.reject(error);
  }
);

// 用户接口
export const authAPI = {
  register: (username: string, password: string) =>
    api.post('/auth/register', { username, password }),
  
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),
  
  getCurrentUser: () =>
    api.get('/auth/me'),
  
  updateUser: (data: { username?: string }) =>
    api.put('/auth/me', data),
  
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.put('/auth/change-password', data),
};

export const dashboardAPI = {
  getOverview: () => api.get('/dashboard/overview'),
  // 每日签到额度：总量 + 各站分别给了多少。默认 31 天，覆盖任意一个自然月。
  getCheckinQuota: (days = 31) =>
    api.get('/dashboard/checkin-quota', { params: { days } }),
  // 按站点看每个账号的额度流水：余额曲线 + 消耗曲线 + 近 24 小时消耗。
  // 只读本地小时桶（由巡检从目标站日志拉取），不打上游。
  getQuotaUsage: (website = '', hours = 48) =>
    api.get('/dashboard/quota-usage', { params: { website, hours } }),
};

// GitHub账号分组接口
export const githubGroupsAPI = {
  // 获取所有分组
  getGroups: () =>
    api.get('/github/groups'),

  // 获取单个分组详情
  getGroup: (id: number) =>
    api.get(`/github/groups/${id}`),

  // 创建分组
  createGroup: (data: {
    name: string;
    description?: string;
    color?: string;
  }) =>
    api.post('/github/groups', data),

  // 更新分组
  updateGroup: (id: number, data: {
    name?: string;
    description?: string;
    color?: string;
  }) =>
    api.put(`/github/groups/${id}`, data),

  // 删除分组
  deleteGroup: (id: number) =>
    api.delete(`/github/groups/${id}`),
};

// GitHub账号接口
export const githubAPI = {
  // 获取所有账号
  getAccounts: () =>
    api.get('/github/accounts'),

  // 获取单个账号详情（包含真实密码和密钥）
  getAccount: (id: number) =>
    api.get(`/github/accounts/${id}`),

  // 创建账号
  createAccount: (data: {
    username?: string;
    email?: string;
    github_username?: string;
    password: string;
    totp_secret: string;
    created_at: string;
    group_id?: number;
  }) =>
    api.post('/github/accounts', data),

  // 更新账号
  updateAccount: (id: number, data: any) =>
    api.put(`/github/accounts/${id}`, data),

  // 删除账号
  deleteAccount: (id: number) =>
    api.delete(`/github/accounts/${id}`),

  batchUpdateGroup: (accountIds: number[], groupId?: number) =>
    api.patch('/github/accounts/batch-group', {
      account_ids: accountIds,
      group_id: groupId ?? null,
    }),

  // 获取TOTP验证码
  getTOTP: (id: number) =>
    api.get(`/github/accounts/${id}/totp`),

  refreshCheckin: (id: number, turnstileToken?: string, website?: string) =>
    api.post(`/github/accounts/${id}/checkin`, null, {
      params: {
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
        ...(website ? { website } : {}),
      },
    }),
  // website 省略时刷新最近一次登录的那个站；一个号同时在 GoRouter 和 Tabi
  // 上时，两站各自独立刷新。
  startCheckin: (id: number, website?: string) =>
    api.post(`/github/accounts/${id}/checkin/jobs`, null, {
      params: website ? { website } : undefined,
    }),
  getCheckinStatus: (accountId: number, jobId: string) =>
    api.get(`/github/accounts/${accountId}/checkin/jobs/${jobId}`),
  // 一键补签：哪些站还欠签到由后端自己挑，被封的号、令牌失效的绑定、
  // 当前整体不可用的站都跳过（对着死站重试只会白花解算器的钱）。
  getPendingCheckins: () =>
    api.get('/github/accounts/checkin/pending'),
  startBatchCheckin: (accountIds?: number[]) =>
    api.post('/github/accounts/checkin/batch-jobs',
      accountIds && accountIds.length ? { account_ids: accountIds } : {}),
  getBatchCheckinStatus: (jobId: string) =>
    api.get(`/github/accounts/checkin/batch-jobs/${jobId}`),
  // 只清除本系统保存的该站令牌/会话/签到记录，目标站账号不受影响
  unlinkSite: (id: number, website: string) =>
    api.delete(`/github/accounts/${id}/sites`, { params: { website } }),

  // 批量获取所有TOTP验证码
  getAllTOTP: () =>
    api.get('/github/totp/batch'),

  // 批量导入账号
  previewBatchImport: (githubUsernames: string[]) =>
    api.post('/github/accounts/batch-import/preview', {
      github_usernames: githubUsernames,
    }),

  batchImport: (accounts: Array<{
    username?: string;
    email?: string;
    github_username?: string;
    password: string;
    totp_secret: string;
    created_at: string;
    group_id?: number;
  }>, options?: {
    queue_oauth_login?: boolean;
    // 单站保留兼容；website_ids 可以一次选多个目标站
    website_id?: number;
    website_ids?: number[];
    group_id?: number;
  }) =>
    api.post('/github/accounts/batch-import', { accounts, ...options }),

  // 给已有账号补登缺失的目标站：勾一批号 + 选站，后端排一个串行OAuth队列，
  // 已经有令牌的站会被跳过，不会重复授权。
  batchLinkSites: (
    accountIds: number[],
    websiteIds: number[],
    skipLinkedSites: boolean = true,
  ) =>
    api.post('/github/accounts/batch-link-sites', {
      account_ids: accountIds,
      website_ids: websiteIds,
      skip_linked_sites: skipLinkedSites,
    }),

  getOAuthQueueStatus: (batchId: string) =>
    api.get(`/github/oauth-queue/${batchId}`),

  cancelOAuthQueue: (batchId: string) =>
    api.post(`/github/oauth-queue/${batchId}/cancel`),

  // 使用选定的API网站发起GitHub OAuth登录
  oauthLogin: (githubAccountId: number, websiteId: number, includeApiKey: boolean = true) =>
    api.post(`/github/oauth-login/${githubAccountId}`, null, {
      params: {
        website_id: websiteId,
        include_api_key: includeApiKey,
      },
    }),
  startOAuthLogin: (githubAccountId: number, websiteId: number, includeApiKey: boolean = true) =>
    api.post(`/github/oauth-login/${githubAccountId}/jobs`, null, {
      params: { website_id: websiteId, include_api_key: includeApiKey },
    }),
  getOAuthLoginStatus: (jobId: string) =>
    api.get(`/github/oauth-login/jobs/${jobId}`),
  // GitHub 账号/浏览器缓存状态检测
  probeGithubStatus: (githubAccountId: number) =>
    api.post(`/github/accounts/${githubAccountId}/github-status`),
  // 检测范围：传 account_ids 只检测这些号；不传 = 全部（旧行为）
  startGithubStatusJob: (accountIds?: number[]) =>
    api.post('/github/accounts/github-status/jobs',
      accountIds && accountIds.length > 0 ? { account_ids: accountIds } : {}),
  getGithubStatusJob: (jobId: string) =>
    api.get(`/github/accounts/github-status/jobs/${jobId}`),
};

// 定时任务接口
export const scheduledTasksAPI = {
  // 获取所有定时任务
  getTasks: () =>
    api.get('/scheduled-tasks/tasks'),
  
  // 获取任务详情
  getTask: (id: number) =>
    api.get(`/scheduled-tasks/tasks/${id}`),
  
  // 创建GitHub OAuth定时任务
  createGitHubOAuthTask: (data: {
    name: string;
    description?: string;
    cron_expression: string;
    github_account_ids: number[];
    target_website?: string;
    retry_count?: number;
    is_active?: boolean;
  }) =>
    api.post('/scheduled-tasks/tasks/github-oauth', data),
  
  // 更新任务
  updateTask: (id: number, data: {
    name?: string;
    description?: string;
    cron_expression?: string;
    task_params?: any;
    is_active?: boolean;
  }) =>
    api.put(`/scheduled-tasks/tasks/${id}`, data),
  
  // 删除任务
  deleteTask: (id: number) =>
    api.delete(`/scheduled-tasks/tasks/${id}`),
  
  // 手动执行任务
  runTask: (id: number) =>
    api.post(`/scheduled-tasks/tasks/${id}/run`),
  
  // 获取任务执行日志
  getTaskLogs: (id: number, limit?: number) =>
    api.get(`/scheduled-tasks/tasks/${id}/logs`, { params: { limit } }),
  
  // 获取余额历史
  getBalanceHistory: (
    id: number,
    params?: { account_id?: number; limit?: number }
  ) =>
    api.get(`/scheduled-tasks/tasks/${id}/balance-history`, { params }),

  // 切换任务状态
  toggleTask: (id: number) =>
    api.post(`/scheduled-tasks/tasks/${id}/toggle`),
};

export const operationLogsAPI = {
  getLogs: (params?: {
    limit?: number;
    status?: string;
    operation?: string;
    keyword?: string;
    // 按目标站过滤；双站之后日志条数翻倍
    website?: string;
  }) => api.get('/logs', { params }),
};

// API网站接口
export const apiWebsiteAPI = {
  // 获取所有API网站
  getWebsites: () =>
    api.get('/api-website/websites'),

  probeVersions: () =>
    api.post('/api-website/websites/probe-versions'),
  
  // 获取单个API网站详情
  getWebsite: (id: number) =>
    api.get(`/api-website/websites/${id}`),
  
  // 创建API网站
  createWebsite: (data: {
    name: string;
    type: string;
    login_url: string;
    username?: string;
    password?: string;
  }) =>
    api.post('/api-website/websites', data),
  
  // 更新API网站
  updateWebsite: (id: number, data: any) =>
    api.put(`/api-website/websites/${id}`, data),
  
  // 删除API网站
  deleteWebsite: (id: number) =>
    api.delete(`/api-website/websites/${id}`),
  
  // 模拟登录
  simulateLogin: (id: number) =>
    api.post(`/api-website/websites/${id}/login`),
  
  // 获取账户信息。默认只读快照，秒开；实时探测（每个绑定的号一次请求）只在点
  // 「刷新账户信息」时做 —— 原来打开对话框就串行探全部号，38 个号能转十几分钟。
  getAccountInfo: (id: number, refresh = false) =>
    api.get(`/api-website/websites/${id}/account-info`, {
      params: refresh ? { refresh: true } : undefined,
    }),
};

// Process-wide new-api monitoring schedule
export const monitorSettingsAPI = {
  getSettings: () =>
    api.get('/monitor-settings'),
  saveSettings: (data: {
    health_check_interval_minutes: number;
    registration_check_interval_minutes: number;
    version_check_interval_minutes: number;
    checkin_times: string[];
    checkin_enabled: boolean;
    timezone: string;
    minimum_balance_to_enable: number;
  }) => api.put('/monitor-settings', data),
};

// API密钥推送接口
export const apiPushAPI = {
  getSettings: () =>
    api.get('/api-push/settings'),

  // 看清源站 /api/pricing 到底给了什么：走的是和版本探测同一条路，
  // 所以能直接回答"上游单价 0 条"是没抓到还是本来就没有按次计费的模型
  probePricing: () =>
    api.get('/api-push/pricing-probe'),

  saveSettings: (data: {
    target_base_url: string;
    admin_access_token?: string;
    admin_user_id: number;
    mappings: Array<{
      website_id: number;
      channel_id: number;
      multi_key_mode: 'polling' | 'random' | 'daily_balance';
      is_active: boolean;
    }>;
  }) => api.put('/api-push/settings', data),

  push: () => api.post('/api-push/push'),

  getLogs: (limit = 50) => api.get('/api-push/logs', { params: { limit } }),
};

// 仓库收藏接口
export const repositoryStarAPI = {
  // 获取所有仓库收藏任务
  getTasks: () =>
    api.get('/repository-star/tasks'),
  
  // 获取单个任务详情
  getTask: (id: number) =>
    api.get(`/repository-star/tasks/${id}`),
  
  // 创建仓库收藏任务
  createTask: (data: {
    repository_url: string;
    description?: string;
    github_account_ids: number[];
    execute_immediately?: boolean;
  }) =>
    api.post('/repository-star/tasks', data),
  
  // 更新任务
  updateTask: (id: number, data: {
    repository_url?: string;
    description?: string;
  }) =>
    api.put(`/repository-star/tasks/${id}`, data),
  
  // 删除任务
  deleteTask: (id: number) =>
    api.delete(`/repository-star/tasks/${id}`),
  
  // 手动执行任务
  executeTask: (id: number, data?: {
    github_account_ids?: number[];
    force_execute?: boolean;
  }) =>
    api.post(`/repository-star/tasks/${id}/execute`, data || {}),

  // 取消收藏任务
  unstarTask: (id: number, data?: {
    github_account_ids?: number[];
  }) =>
    api.post(`/repository-star/tasks/${id}/unstar`, data || {}),

  // 获取任务执行记录
  getTaskRecords: (id: number) =>
    api.get(`/repository-star/tasks/${id}/records`),

  // 获取任务队列状态
  getQueueStatus: (id: number) =>
    api.get(`/repository-star/tasks/${id}/queue-status`),

  // 获取整体队列信息
  getQueueInfo: () =>
    api.get('/repository-star/queue/info'),

  // 取消队列中的任务
  cancelTask: (id: number) =>
    api.post(`/repository-star/tasks/${id}/cancel`),

  // 批量导入仓库
  batchImport: (data: {
    repository_urls: string[];
    github_account_ids: number[];
    execute_immediately?: boolean;
  }) =>
    api.post('/repository-star/batch-import', data),

  // 批量执行任务
  batchExecute: (data: {
    task_ids: number[];
    force_execute?: boolean;
  }) =>
    api.post('/repository-star/batch-execute', data),
};

// 系统信息接口
export const systemAPI = {
  // 获取版本信息
  getVersion: () =>
    api.get('/version'),

  // 健康检查
  healthCheck: () =>
    api.get('/health'),

  // 全系统时区。这是部署级设置，不是浏览器偏好——签到日界线、cron 计算、界面显示
  // 都读同一个值。读接口不要求登录，前端在拿到登录态之前就要用它渲染时间。
  getTimezone: () =>
    api.get('/system-settings/timezone'),

  setTimezone: (timezone: string) =>
    api.put('/system-settings/timezone', { timezone }),

  listTimezones: () =>
    api.get('/system-settings/timezones'),
};

// 解算器设置（2captcha 等付费 Turnstile 解算服务）
export const solverSettingsAPI = {
  getSettings: () =>
    api.get('/solver-settings'),
  saveSettings: (data: {
    solver_type: string;
    is_enabled: boolean;
    api_key?: string;
  }) => api.put('/solver-settings', data),
};

// 网络与超时设置。以前只能靠环境变量，改一次要重启容器；这些值需要按线路现场调。
export const networkSettingsAPI = {
  getSettings: () =>
    api.get('/network-settings'),
  saveSettings: (data: {
    github_connect_timeout?: number;
    github_read_timeout?: number;
    github_retry_total?: number;
    new_api_connect_timeout?: number;
    new_api_read_timeout?: number;
    oauth_site_timeout?: number;
    new_api_critical_limit?: number;
    new_api_critical_window?: number;
    new_api_critical_margin?: number;
  }) => api.put('/network-settings', data),
};

// 出口代理（DataImpulse 住宅代理）。目标站按出口 IP 算配额，撞 429 就换 IP。
export const proxySettingsAPI = {
  getSettings: () => api.get('/proxy-settings'),
  saveSettings: (data: {
    is_enabled?: boolean;
    host?: string;
    port?: number;
    login?: string;
    password?: string;
    countries?: string;
    protocol?: string;
    session_ttl?: number;
    rotate_on_rate_limit?: boolean;
    apply_to_target?: boolean;
    apply_to_github?: boolean;
    apply_to_patrol?: boolean;
    apply_to_push?: boolean;
  }) => api.put('/proxy-settings', data),
  // 通过代理查一次出口 IP，配错了在这里就能看出来
  test: () => api.post('/proxy-settings/test'),
  rotate: () => api.post('/proxy-settings/rotate'),
};

// FastAPI 的 422 把 detail 给成对象数组（每项 {type, loc, msg, ...}）。直接丢给
// antd 的 message.error 会让 React 抛 "Objects are not valid as a React child"，
// 结果是提示整个渲染不出来——用户点了保存，既没成功也没失败，看着像按钮坏了。
export const errorText = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean);
    if (parts.length > 0) return parts.join('；');
  }
  if (detail && typeof detail === 'object') {
    const msg = (detail as any).msg;
    if (typeof msg === 'string' && msg) return msg;
  }
  const message = error?.response?.data?.message || error?.message;
  return typeof message === 'string' && message ? message : fallback;
};

export default api;
